import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type AddressDto } from "@modules/address/addressDto"
import { word } from "@modules/core/utils/i18n"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { formatPhoneNumber } from "@modules/utils/numberUtils"
import dayjs from "dayjs"
import { type CountryCode } from "libphonenumber-js"

type InvoiceDetailsProps = {
  transactionDetails: TransactionDetailsEntity
  className?: string
} & React.ComponentProps<"div">

export function InvoiceDetails({ transactionDetails, className, ...props }: InvoiceDetailsProps) {
  const invoiceRow = (fieldLabel: string, fieldValue?: string | React.ReactNode) =>
    fieldValue && (
      <div className="flex flex-col items-baseline gap-1 overflow-hidden">
        <Text fontStyle="BS" className="text-textDefault-body">
          {fieldLabel}
        </Text>
        <div className="flex w-full items-baseline overflow-hidden">
          <Text fontStyle="BM" className="overflow-hidden overflow-ellipsis text-textDefault-heading">
            {fieldValue}
          </Text>
        </div>
      </div>
    )

  const formatAddress = (address: AddressDto | null) => {
    if (!address) return <></>

    return (
      <>
        {address.address + " " + (address.additionalInformation ?? "")}
        <br />
        {address.postalCode + " " + address.city}
      </>
    )
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <div className="mb-4 flex flex-row justify-between gap-3">
        {invoiceRow(word("finances.page.table.header.invoice"), transactionDetails.invoiceNumber)}

        {invoiceRow(
          word("finances.details.issue_date"),
          <>
            {dayjs(transactionDetails.date).format("L")}
            <Text fontStyle="BS" className="ml-1 overflow-hidden overflow-ellipsis text-textDefault-heading">
              {dayjs(transactionDetails.date).format("LT")}
            </Text>
          </>,
        )}

        {transactionDetails.user?.boxMemberNumber &&
          invoiceRow(word("finances.details.membership_number"), `${transactionDetails.user.boxMemberNumber}`)}
      </div>

      <div className="mb-3 flex flex-row justify-between gap-3">
        {transactionDetails.box
          ? invoiceRow(word("finances.details.box"), `${transactionDetails.box.name}`)
          : transactionDetails.user
            ? invoiceRow(
                word("finances.details.user"),
                `${transactionDetails.user.firstname} ${transactionDetails.user.lastname}`,
              )
            : invoiceRow(word("finances.details.user"), `-`)}

        {transactionDetails.box?.phoneNumber
          ? invoiceRow(
              word("global.phoneNumber"),
              formatPhoneNumber(
                transactionDetails.box.phoneNumber.number,
                transactionDetails.box.phoneNumber.countryCode as CountryCode,
              ),
            )
          : transactionDetails.user?.phoneNumber
            ? invoiceRow(
                word("global.phoneNumber"),
                formatPhoneNumber(
                  transactionDetails.user.phoneNumber.number,
                  transactionDetails.user.phoneNumber.countryCode as CountryCode,
                ),
              )
            : invoiceRow(word("global.phoneNumber"), `-`)}

        {transactionDetails.box?.address
          ? invoiceRow(word("box.form.address.address.label"), formatAddress(transactionDetails.box.address))
          : transactionDetails.user?.address
            ? invoiceRow(word("box.form.address.address.label"), formatAddress(transactionDetails.user.address))
            : invoiceRow(word("box.form.address.address.label"), `-`)}
      </div>
    </div>
  )
}
